import { QueryClient } from '@tanstack/react-query';

import { isApiError, isRateLimitError, isUnauthorizedError } from '@/shared/api';

/**
 * The shared QueryClient.
 *
 * Server state lives here and nowhere else. Retry policy is deliberately
 * conservative for a payments app: never retry a 4xx, never retry a rate-limited
 * call (the client already holds a local cooldown — retrying would just burn the
 * shared 120 req/min budget), and never retry mutations at all, because a retried
 * mutation is a duplicated transfer unless the Idempotency-Key is identical, and
 * that is the call site's decision to make, not a default.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (isRateLimitError(error) || isUnauthorizedError(error)) return false;
          if (isApiError(error) && error.status !== undefined && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000)
      },
      mutations: {
        retry: false
      }
    }
  });
}
